import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayM, LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useCheckForMobilePushNotifications } from '@sorare/core/src/hooks/useCheckForMobilePushNotifications';
import { glossary } from '@sorare/core/src/lib/glossary';

import appIcon from './assets/appIcon.webp';
import notificationImage from './assets/notificationImage.webp';

const Wrapper = styled(Vertical)`
  height: 100%;
  overflow: hidden;
  gap: var(--triple-unit);
  justify-content: space-between;

  background: linear-gradient(
    to bottom,
    rgba(44, 43, 112, 0.8) 0%,
    var(--c-black) 50%
  );
`;

const Content = styled(Vertical)`
  flex: 1;
  justify-content: space-around;
  padding: var(--double-unit);
`;
const FooterWrapper = styled(Vertical)`
  bottom: 0;
  z-index: 2;
  position: sticky;
  padding: var(--double-unit);
`;

const ImageWrapper = styled(Vertical)`
  position: relative;
`;

const Image = styled(Img)`
  width: 100%;
  max-width: min(80%, 400px);
`;

const NotificationsWrapper = styled(Vertical)`
  width: 100%;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`;

const NotificationPopup = styled(Horizontal)`
  width: 90%;
  background: var(--c-nd-100);
  backdrop-filter: blur(10px);
  padding: var(--intermediate-unit);
  border-radius: var(--double-and-a-half-unit);

  &.small {
    transform: scale(0.75);
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const NBAEnableNotificationsPrompt = ({ open, onClose }: Props) => {
  const checkForMobilePushNotifications = useCheckForMobilePushNotifications();
  const [messagePosted, setMessagePosted] = useState(false);

  return (
    <Dialog open={open} maxWidth="xs">
      <Wrapper>
        <Content center>
          <Vertical center>
            <DisplayM>
              <FormattedMessage defaultMessage="Don’t miss the action!" />
            </DisplayM>
            <LabelL color="var(--c-nd-600)" className="text-center">
              <FormattedMessage defaultMessage="Stay up-to-date with your lineup performance and don’t miss a single reward" />
            </LabelL>
          </Vertical>
          <ImageWrapper center>
            <Image src={notificationImage} />
            <NotificationsWrapper center>
              <NotificationPopup>
                <Img src={appIcon} />
                <Vertical gap={0.5}>
                  <LabelM bold>
                    <FormattedMessage defaultMessage="You won 2 rewards!" />
                  </LabelM>
                  <LabelM>
                    <FormattedMessage defaultMessage="🔥 Tap to claim your rewards now" />
                  </LabelM>
                </Vertical>
              </NotificationPopup>
              <NotificationPopup className="small">
                <Img src={appIcon} />
                <Vertical gap={0.5}>
                  <LabelM bold>
                    <FormattedMessage defaultMessage="Daily Hoops is complete" />
                  </LabelM>
                  <LabelM>
                    <FormattedMessage defaultMessage="Come check your results!" />
                  </LabelM>
                </Vertical>
              </NotificationPopup>
            </NotificationsWrapper>
          </ImageWrapper>
        </Content>
        <FooterWrapper center gap={2}>
          <Button
            size="large"
            fullWidth
            color="secondary"
            onClick={() => {
              if (!messagePosted) {
                checkForMobilePushNotifications();
                setMessagePosted(true);
              } else {
                onClose();
              }
            }}
          >
            {messagePosted ? (
              <FormattedMessage {...glossary.continue} />
            ) : (
              <FormattedMessage defaultMessage="Turn on notifications" />
            )}
          </Button>
          {!messagePosted && (
            <Button
              size="large"
              fullWidth
              color="transparent"
              onClick={() => {
                onClose();
              }}
            >
              <FormattedMessage defaultMessage="Not now" />
            </Button>
          )}
        </FooterWrapper>
      </Wrapper>
    </Dialog>
  );
};
